import React from 'react';
import { Badge } from 'src/design/atoms/Badge';
import { Select } from 'src/design/atoms/Select';
import { JobUserDto } from 'src/api/FiretrolApiClient';
import { useJobTeamByOffice } from 'src/api/queries/jobs/useJobTeam';
import { useJobs } from 'src/hooks/useJobs';

const NO_TEAM: JobUserDto[] = [];

export interface IJobUserSelectProps {
  onChange?: (value: JobUserDto | null) => void;
  hideLabel?: boolean;
  small?: boolean;
  selectedUserId?: number | null;
  hideRole?: boolean;
  disabled?: boolean;
  placeholder?: string;
  office?: string | null;
  supervisorId?: number | null;
}

/** @registry Single-select dropdown of users assigned to the current job. */
export const JobUserSelect: React.FC<IJobUserSelectProps> = ({
  onChange,
  placeholder = 'Select a user...',
  disabled = false,
  hideRole = false,
  selectedUserId,
  small = false,
  hideLabel = false,
  office,
  supervisorId,
}) => {
  const { team } = useJobs();
  const officeTeam = useJobTeamByOffice(office).data ?? NO_TEAM;
  const [value, setValue] = React.useState<JobUserDto | null>(null);

  // Derive options so they track `team` as it resolves (non-office callers) and
  // the office-scoped fetch (office callers) — never a one-time state copy.
  const rawOptions = office ? officeTeam : team;

  // Filter options by supervisor if supervisorId is provided
  const options = React.useMemo(() => {
    if (!supervisorId) return rawOptions;
    return rawOptions.filter(
      (teamUser) =>
        teamUser.user?.supervisorId === supervisorId ||
        teamUser.user?.supervisor?.supervisorId === supervisorId,
    );
  }, [rawOptions, supervisorId]);

  React.useEffect(() => {
    if (selectedUserId) {
      const selected =
        options.find((teamUser) => teamUser.userId === selectedUserId) || null;
      setValue(selected);
    } else {
      setValue(null);
    }
  }, [options, selectedUserId]);
  // Transform options data to Mantine's expected format
  const selectData = React.useMemo(() => {
    return options.map((teamUser) => ({
      value: String(teamUser.id),
      label: `${teamUser.user?.firstName} ${teamUser.user?.lastName}`,
    }));
  }, [options]);

  // Get the current value as a string ID for Mantine
  const mantineValue = value ? String(value.id) : null;

  const handleChange = (selectedValue: string | null) => {
    if (selectedValue) {
      const selectedTeamUser = options.find(
        (teamUser) => String(teamUser.id) === selectedValue,
      );
      if (onChange) {
        onChange(selectedTeamUser || null);
      }
    } else {
      // Handle clear
      if (onChange) {
        onChange(null);
      }
    }
  };

  // Custom render option to show user name and roles
  const renderOption = ({
    option,
    checked,
  }: {
    option: { value: string; label: string };
    checked: boolean;
  }) => {
    const teamUser = options.find((o) => String(o.id) === option.value);
    if (!teamUser || !teamUser.user) return <div>{option.label}</div>;

    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          width: '100%',
        }}
      >
        {checked && <span>✓</span>}
        <div
          style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}
        >
          {teamUser.user.firstName} {teamUser.user.lastName}
          {!hideRole && teamUser.user.userOrganizationRole && (
            <span>
              {teamUser.user.userOrganizationRole.map((r) => {
                return (
                  <Badge
                    key={r.id}
                    color="blue"
                    variant="filled"
                    style={{ marginLeft: '4px' }}
                  >
                    {r.organizationRole?.name}
                  </Badge>
                );
              })}
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <Select
      label={!hideLabel ? 'Users' : undefined}
      placeholder={placeholder}
      data={selectData}
      value={mantineValue}
      onChange={handleChange}
      searchable
      clearable
      disabled={disabled}
      renderOption={renderOption}
      className={small ? 'single-small' : ''}
    />
  );
};
